import { type PromoCodeList, type PromoCodeListRequest } from "@repo/common";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";

import { normalizeError } from "lib/error.utils";
import { queryKeys } from "lib/query-keys";
import { listPromoCodes } from "services/promo-codes/promoCodes";

export interface UsePromoCodesListParams {
  params: PromoCodeListRequest;
  enabled?: boolean;
}

export interface UsePromoCodesListData {
  data?: PromoCodeList;
  loading: boolean;
  refetching: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  error?: Error;
  fetchMore: () => Promise<void>;
  refetch: () => Promise<void>;
}

const defaultOptions: PromoCodeListRequest = {
  limit: 50,
  sort: "createdOnIsoDate",
  sortDirection: "desc",
  cursorDocId: undefined,
};

export const promoCodesListQueryKeys = queryKeys.promoCodes;

export const usePromoCodesList = ({ params, enabled = true }: UsePromoCodesListParams): UsePromoCodesListData => {
  const limit = params.limit ?? defaultOptions.limit;
  const sort = params.sort ?? defaultOptions.sort;
  const sortDirection = params.sortDirection ?? defaultOptions.sortDirection;

  const listOptions = useMemo(
    () => ({
      limit,
      sort,
      sortDirection,
    }),
    [limit, sort, sortDirection],
  );

  const {
    data,
    error: queryError,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isRefetching,
    isFetchingNextPage,
    isFetching,
    refetch,
  } = useInfiniteQuery({
    queryKey: queryKeys.promoCodes.list(listOptions),
    queryFn: ({ pageParam }) =>
      listPromoCodes({
        ...listOptions,
        cursorDocId: pageParam,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => (lastPage.hasNextPage ? lastPage.cursorDocId : undefined),
    enabled,
  });

  const mergedData = useMemo((): PromoCodeList | undefined => {
    if (!data) {
      return undefined;
    }

    const lastPage = data.pages.length > 0 ? data.pages[data.pages.length - 1] : undefined;

    if (!lastPage) {
      return undefined;
    }

    return {
      promoCodes: data.pages.flatMap((page) => page.promoCodes),
      total: lastPage.total,
      cursorDocId: lastPage.cursorDocId,
      hasNextPage: lastPage.hasNextPage,
    };
  }, [data]);

  const fetchMore = useCallback(async (): Promise<void> => {
    if (!hasNextPage || isFetching || isFetchingNextPage) {
      return;
    }

    await fetchNextPage();
  }, [fetchNextPage, hasNextPage, isFetching, isFetchingNextPage]);

  const refetchList = useCallback(async (): Promise<void> => {
    await refetch();
  }, [refetch]);

  const error = queryError ? normalizeError(queryError) : undefined;

  return {
    data: mergedData,
    loading: enabled ? isLoading : false,
    refetching: isRefetching,
    loadingMore: isFetchingNextPage,
    hasMore: hasNextPage ?? false,
    error,
    fetchMore,
    refetch: refetchList,
  };
};
